import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_OPEN_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { useDebounce } from '@/hooks/useDebounce'
import { clientsApi } from '@/services/clients'
import { PICKER_LIMIT } from '@/lib/listFetch'
import { cn } from '@/lib/utils'
import { ClientNameWithProspect } from '@/components/shared/StatusBadges'

interface Props {
  // Selected client id (controlled). Undefined = "all clients".
  value?: string
  // Display label for the current selection (kept in the URL so it survives a
  // refresh without an extra fetch); falls back to the placeholder.
  valueLabel?: string
  onChange: (id?: string, name?: string) => void
  placeholder: string
  searchPlaceholder: string
  clearLabel: string
  emptyLabel: string
  errorLabel: string
  triggerClassName?: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  // Load the initial client list as soon as the filter mounts so opening it
  // can reuse the cached result. Search requests remain demand-driven.
  preload?: boolean
}

const triggerClass =
  'h-10 w-auto shrink-0 min-w-[140px] justify-between gap-2 rounded-xl border-[#e7e9ef] py-3 pl-4 pr-3 text-sm font-normal text-foreground focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:ring-0 [&_svg]:size-4'

// Searchable single-select client filter (payments list toolbar, Figma
// 1126:15356). Mirrors ClientMultiSelectFilter's UX (button + popover,
// debounced server-side search, click-outside close) but selects a single
// client — the payments backend filters on a single `client_id` uuid — with an
// "all clients" reset row.
export function ClientSelectFilter({
  value,
  valueLabel,
  onChange,
  placeholder,
  searchPlaceholder,
  clearLabel,
  emptyLabel,
  errorLabel,
  triggerClassName,
  open: controlledOpen,
  onOpenChange,
  preload = false,
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const [term, setTerm] = useState('')
  const debounced = useDebounce(term, 300)
  const ref = useRef<HTMLDivElement>(null)
  const openRef = useRef(open)

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [controlledOpen, onOpenChange]
  )

  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!openRef.current) return
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (openRef.current && e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [setOpen])

  const { data, isFetching, isError } = useQuery({
    queryKey: ['clients', 'single-filter', debounced],
    queryFn: () =>
      clientsApi.list({
        search: debounced,
        limit: PICKER_LIMIT,
        sort_column: 'name',
        sort_order: 'asc',
      }),
    enabled: open || preload,
  })
  const clients = data?.data ?? []
  const label = value ? (valueLabel ?? placeholder) : placeholder

  return (
    <div ref={ref} className='relative shrink-0'>
      <Button
        type='button'
        variant='filter'
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={cn(
          triggerClass,
          triggerClassName,
          FORM_FILTER_HOVER_CLASS,
          value && 'border-primary text-primary',
          open && FORM_CONTROL_OPEN_CLASS
        )}
      >
        <span className='truncate'>{label}</span>
        <ChevronDown className='ml-2 size-4 shrink-0 opacity-60' />
      </Button>

      {open && (
        <div className='absolute left-0 z-50 mt-1 w-[280px] rounded-xl border border-[#e7e9ef] bg-popover p-2 text-popover-foreground shadow-md'>
          <div className='relative mb-2'>
            <Search className='absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
            <input
              autoFocus
              value={term}
              onChange={e => setTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className='h-10 w-full rounded-xl border border-[#e7e9ef] bg-transparent pl-8 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring'
            />
          </div>

          <button
            type='button'
            onClick={() => {
              onChange(undefined, undefined)
              setOpen(false)
            }}
            className='mb-1 flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm hover:bg-accent'
          >
            {clearLabel}
            {!value && <Check className='size-4' />}
          </button>

          <div className='max-h-64 overflow-y-auto'>
            {isError ? (
              <p className='px-2 py-3 text-center text-sm text-destructive'>
                {errorLabel}
              </p>
            ) : clients.length === 0 && !isFetching ? (
              <p className='px-2 py-3 text-center text-sm text-muted-foreground'>
                {emptyLabel}
              </p>
            ) : (
              clients.map(c => (
                <button
                  key={c.id}
                  type='button'
                  onClick={() => {
                    onChange(c.id, c.name)
                    setOpen(false)
                  }}
                  className='flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm hover:bg-accent'
                >
                  <ClientNameWithProspect name={c.name} category={c.category} />
                  {c.id === value && <Check className='size-4 shrink-0' />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientSelectFilter
